import { customerMenu, drinkDescription, drinkName, hoursLabel } from './customerMenu';
import { SignInButton, UserButton } from "@clerk/clerk-react";
import {
  ArrowRight,
  Check,
  Flame,
  Minus,
  Plus,
  Search,
  ShieldCheck,
  ShoppingBag,
  Snowflake,
  Trash2
} from "lucide-react";
import { lazy, useEffect, useMemo, useRef, useState } from "react";
import { canonicalItem, type MenuProduct } from "../shared/domain";
import { selectionsForChannel } from "../shared/customerPackaging";
import { groupMenuProducts, temperatures, type MenuDrink } from '../shared/drinkGroups';
import { Dialog } from "./Dialog";
import { MenuFeatures, RecipeFeatures } from './MenuFeatures';
import { PickupDetails, usePickupDetails } from './PickupDetails';
import { SavedDrinks, useSavedDrinks } from './SavedDrinksPanel';
import { currentOrderKey, forgetCurrentOrder, rememberCurrentOrder, useCurrentOrder } from './currentOrder';
import { StaffGate, useAppData } from "./data";
import { useI18n } from "./i18n";
import { RouteBoundary } from "./RouteBoundary";
import { pickupProfileKey } from './pickupProfile';
import { RecipeButton } from './recipes';
import { savedDrinksKey, validateSavedItem, type SavedFavorite } from './savedDrinks';
import "./styles.css";
const uid = () => crypto.randomUUID();
const MENU_SECTIONS = ["Coffee", "Matcha & Tea"];
const Admin = lazy(() => import('./Admin').then(module => ({ default: module.Admin })));
const Barista = lazy(() => import('./Barista').then(module => ({ default: module.Barista })));
const Receipt = lazy(() => import('./Receipt').then(module => ({ default: module.Receipt })));
const SetupAccount = lazy(() => import('./invitations').then(module => ({ default: module.SetupAccount })));
export function readUnifiedCart(mode: string, channel: string) {
  const read = (key: string) => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      return Array.isArray(value) ? value : null;
    } catch { return null; }
  };
  const saved = read(`tequisa-cart-${mode}`);
  if (saved) return saved;
  const preferred = read(`tequisa-cart-${mode}-${channel}`);
  return preferred?.length ? preferred : read(`tequisa-cart-${mode}-${channel === 'employee' ? 'public' : 'employee'}`) ?? [];
}
function readPricingReview(mode: string): { identity: string; required: boolean } | null {
  try {
    const value = JSON.parse(localStorage.getItem(`tequisa-cart-${mode}-pricing`) || 'null');
    return typeof value?.identity === 'string' && typeof value?.required === 'boolean' ? value : null;
  } catch { return null; }
}
function MenuDescription({ name, description }: { name: string; description: string }) {
  const { locale } = useI18n();
  return <p>{drinkDescription(locale, name, description)}</p>;
}
function MenuProductCard({ card, index, channel, data, disabled, onChoose }: {
  card: MenuDrink; index: number; channel: string; data: any; disabled: boolean; onChoose: (product: MenuProduct) => void;
}) {
  const { t, money } = useI18n();
  const [selectedId, setSelectedId] = useState<string>();
  const product = card.variants.find(p => p.id === selectedId && p.available) ?? card.variants.find(p => p.available) ?? card.variants[0];
  const temperatureChoices = card.variants.length > 1 && card.variants.every(p => p.temperature);
  const blocked = disabled || !product.available;
  return <article className="product-tile">
    <button type="button" className="product-card" aria-label={t('menu.customizeDrink', { name: card.name })} onClick={() => onChoose(product)} disabled={blocked}>
      <div className={"art c" + ((index % 6) + 1)}>
        {!card.available && <span className="product-tag">{t('menu.soldOut')}</span>}
        {card.imageUrl ? <img src={card.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <CupArt index={index} name={card.name} />}
      </div>
      <div className="product-body"><h3>{card.name}</h3><MenuDescription name={card.name} description={card.description} /><RecipeFeatures products={card.variants} channel={channel} /></div>
    </button>
    <div className="product-configure">
      {temperatureChoices ? <div className={`temperature-selector ${product.temperature}`} role="group" aria-label={t('menu.drinkTemperature', { name: card.name })}>
        <span className="temperature-selector-highlight" aria-hidden="true" />
        {card.variants.map(p => <button type="button" className="temperature-select" key={p.id} aria-pressed={p.id === product.id} disabled={disabled || !p.available} onClick={() => setSelectedId(p.id)}>
          {p.temperature === 'hot' ? <Flame size={15} aria-hidden="true" /> : <Snowflake size={15} aria-hidden="true" />}
          <span>{t(`temperature.${p.temperature}`)}{!p.available && <small>{t('temperature.soldOut')}</small>}</span>
        </button>)}
      </div> : product.temperature && <div className={`temperature-fixed ${product.temperature}`}>
        {product.temperature === 'hot' ? <Flame size={15} aria-hidden="true" /> : <Snowflake size={15} aria-hidden="true" />}
        <span>{t(`temperature.${product.temperature}`)}{!product.available && ` · ${t('temperature.soldOut')}`}</span>
      </div>}
      <button type="button" className="temperature-order primary product-customize" disabled={blocked} onClick={() => onChoose(product)}>
        <span>{!product.available ? t('temperature.soldOut') : product.price === null ? t('menu.chooseOptions') : `${t('menu.customize')} · ${money(product.price)}`}</span><ArrowRight size={16} aria-hidden="true" />
      </button>
    </div>
    {data.canReadRecipes && <div className="product-recipe-action">{card.variants.map(p => <div key={p.id}>{p.temperature && <span>{t(`temperature.${p.temperature}`)} </span>}<RecipeButton data={data} productId={p.id} selections={p.defaults} /></div>)}</div>}
  </article>;
}
function CupArt({ index = 0, name = "" }: { index?: number; name?: string }) {
  const dark = /americano|chocolate|cokelat/i.test(name);
  const matcha = /matcha/i.test(name);
  return (
    <svg viewBox="0 0 160 160" aria-hidden="true">
      <defs>
        <linearGradient id={"drink" + index} x1="0" x2="1">
          <stop stopColor={dark ? "#573b2c" : matcha ? "#9baa76" : "#d7b38a"} />
          <stop
            offset=".5"
            stopColor={dark ? "#76503b" : matcha ? "#c2cca1" : "#e4c8a5"}
          />
          <stop
            offset="1"
            stopColor={dark ? "#422e23" : matcha ? "#7f945b" : "#c8986c"}
          />
        </linearGradient>
        <linearGradient id={"glass" + index}>
          <stop stopColor="#ffffff" stopOpacity=".7" />
          <stop offset=".35" stopColor="#fff" stopOpacity=".08" />
          <stop offset="1" stopColor="#fff" stopOpacity=".35" />
        </linearGradient>
      </defs>
      <ellipse cx="81" cy="139" rx="40" ry="8" fill="#80705b" opacity=".12" />
      <path
        d="M48 45h65l-8 86q-24 15-49 0Z"
        fill={"url(#drink" + index + ")"}
      />
      <path
        d="M48 45h65l-2 28q-30 5-60-1Z"
        fill={dark ? "#3a2a23" : "#ba855b"}
      />
      <ellipse
        cx="80.5"
        cy="45"
        rx="32.5"
        ry="10"
        fill={dark ? "#4e3529" : "#ad744b"}
      />
      <g fill="#f8f1d8" opacity=".6" stroke="#fff" strokeOpacity=".65">
        <rect
          x="59"
          y="37"
          width="20"
          height="13"
          rx="3"
          transform="rotate(-12 68 44)"
        />
        <rect
          x="83"
          y="39"
          width="19"
          height="13"
          rx="3"
          transform="rotate(18 91 44)"
        />
        <rect
          x="70"
          y="52"
          width="17"
          height="14"
          rx="3"
          transform="rotate(8 80 56)"
        />
      </g>
      <path
        d="M49 45h64l-8 87q-23 14-48 0Z"
        fill={"url(#glass" + index + ")"}
        stroke="#fff"
        strokeOpacity=".75"
      />
      <path d="m96 11-12 77" stroke="#66755a" strokeWidth="4" />
      <path
        d="M58 82q22 7 46 0l-3 35q-19 8-40 0Z"
        fill="#f5f0df"
        opacity=".94"
      />
      <circle cx="81" cy="100" r="10" fill="#31543d" />
      <path d="M77 96q8-3 7 5q-1 6-7 3Z" fill="none" stroke="#f6f1df" />
      <path d="M85 98q5 0 2 4h-2" fill="none" stroke="#f6f1df" />
    </svg>
  );
}
export default function App() {
  const { t, toggleLocale, translateMessage } = useI18n();
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const cb = () => setPath(location.pathname);
    window.addEventListener("popstate", cb);
    return () => window.removeEventListener("popstate", cb);
  }, []);
  function go(url: string) {
    history.pushState({}, "", url);
    setPath(url);
    window.scrollTo(0, 0);
  }
  useEffect(() => {
    if (path === '/employee') {
      history.replaceState({}, '', '/order' + location.search + location.hash);
      setPath('/order');
    }
  }, [path]);
  const data: any = useAppData();
  const receiptKey = currentOrderKey(data.mode, data.pricingIdentity);
  const currentReceipt = useCurrentOrder(receiptKey);
  const receiptScope = useRef<string | null>(null);
  useEffect(() => {
    if (data.pricingLoading || data.authLoaded === false) return;
    if (receiptScope.current && receiptScope.current !== receiptKey) {
      forgetCurrentOrder(receiptScope.current);
      if (location.pathname.startsWith('/receipt/')) go('/order');
    }
    receiptScope.current = receiptKey;
  }, [receiptKey, data.pricingLoading, data.authLoaded]);
  const channel = data.channel ?? 'public';
  const [toast, setToast] = useState("");
  function notify(t: string) {
    setToast(t);
    setTimeout(() => setToast(""), 4000);
  }
  const staff = path === "/admin" || path === "/barista";
  return (
    <>
      <header className="topbar">
        <a
          href="/order"
          className="brand"
          onClick={(e) => {
            e.preventDefault();
            go("/order");
          }}
        >
          <img
            className="brand-logo"
            src="/b16-logo.png"
            alt="B16 Coffee by Tequisa"
            width="74"
            height="74"
          />
        </a>
        <nav className="nav" aria-label={t("nav.main")}>
          {([
            ["/order", t("nav.menu"), true],
            [
              "/barista",
              t("nav.barista"),
              !data.accessLoading && ["admin", "barista"].includes(data.role),
            ],
            [
              "/admin",
              t("nav.admin"),
              !data.accessLoading && data.role === "admin",
            ],
          ] as [string, string, boolean][]).filter(([, , visible]) => visible).map(([p, l]) => (
            <a
              className={path === p ? "active" : ""}
              href={p}
              key={p}
              onClick={(e) => {
                e.preventDefault();
                go(p);
              }}
            >
              {l}
            </a>
          ))}
          {currentReceipt && !data.pricingLoading && <a href={`/receipt/${encodeURIComponent(currentReceipt)}`} onClick={e => { e.preventDefault(); go(`/receipt/${encodeURIComponent(currentReceipt)}`); }}>{t('receipt.current')}</a>}
        </nav>
        {data.mode === "live" && data.authLoaded && (
          <div className="account-control" aria-label={t("nav.account")}>
            {data.signedIn ? (
              <UserButton showName />
            ) : (
              <SignInButton mode="modal">
                <button type="button">{t("nav.signIn")}</button>
              </SignInButton>
            )}
          </div>
        )}
        <button
          className="language-toggle"
          type="button"
          onClick={toggleLocale}
          aria-label={t("language.switchTo")}
          title={t("language.switchTo")}
        >
          <span aria-hidden="true">{t("language.short")}</span>
        </button>
      </header>
      {data.mode === "demo" && (
        <div className="demo">
          <span>{t("demo.title")}</span>
          <span>{t("demo.tagline")}</span>
        </div>
      )}
      <main className="shell">
        {data.error && (
          <div role="alert" className="error">
            {translateMessage(data.error)}
          </div>
        )}
        <RouteBoundary key={path}>
        {path === "/setup" ? (
          data.mode === "live" ? <SetupAccount /> : <p>{t("provider.closed")}</p>
        ) : data.loading && (staff || path.startsWith("/receipt/")) && !data.menuProducts?.length ? (
          <div className="empty">{t("common.loadingStation")}</div>
        ) : path.startsWith("/receipt/") && (data.pricingLoading || (receiptScope.current !== null && receiptScope.current !== receiptKey)) ? (
          <div role="status">{t('common.loadingStation')}</div>
        ) : path.startsWith("/receipt/") ? (
          <Receipt
            data={data}
            credential={decodeURIComponent(path.split("/")[2])}
            go={go}
          />
        ) : staff ? (
          <StaffGate requireAdmin={path === "/admin"}>
            <>
              {path === "/barista" ? (
                <Barista data={data} notify={notify} />
              ) : (
                <Admin data={data} notify={notify} />
              )}
            </>
          </StaffGate>
        ) : (
          <Menu channel={channel} data={data} go={go} />
        )}
        </RouteBoundary>
      </main>
      {toast && (
        <div role="status" className="toast">
          {toast}
        </div>
      )}
    </>
  );
}
export function Menu({ channel, data, go }: any) {
  const { t, money, locale, translateMessage, translateOptionLabel } = useI18n();
  const products = useMemo(() => customerMenu(data.menuProducts || []), [data.menuProducts]);
  const ALL = "__all__";
  const [category, setCategory] = useState(ALL);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingFavorite, setEditingFavorite] = useState<SavedFavorite | null>(null);
  const savedKey = savedDrinksKey(data.mode, data.pricingIdentity);
  const saved = useSavedDrinks(savedKey);
  const [savedBusy, setSavedBusy] = useState(false);
  const savedRequest = useRef(0);
  const savedLocked = useRef(false);
  const pricingReady = !data.pricingLoading;
  const paymentReadyRef=useRef(true);paymentReadyRef.current=data.mode!=='live'||data.paymentAvailable===true;
  const [initialized, setInitialized] = useState(pricingReady);
  const [storedCart, setCart] = useState<any[]>(() => pricingReady ? readUnifiedCart(data.mode, channel) : []);
  const cart = useMemo(() => storedCart.map(item => ({
    ...item,
    selections: selectionsForChannel(channel, item.selections),
    optionLabels: channel === "employee" ? item.optionLabels : item.optionLabels?.map(
      (label: string) => ["Gelas sendiri", "Own cup"].includes(label) ? "Takeaway" : label,
    ),
  })), [storedCart, channel]);
  const pricingIdentity = JSON.stringify([data.pricingIdentity ?? data.mode, channel]);
  const receiptContext = JSON.stringify([pricingIdentity, pricingReady]);
  const checkoutOwner = useRef({context:receiptContext,epoch:0});
  if (checkoutOwner.current.context !== receiptContext) checkoutOwner.current = {context:receiptContext,epoch:checkoutOwner.current.epoch+1};
  useEffect(() => () => { checkoutOwner.current.epoch++; }, []);
  const context = JSON.stringify([pricingIdentity, pricingReady, cart, products]);
  // A monotonic epoch also rejects requests across guest → account → guest changes.
  const requestContext = useRef({ context, epoch: 0 });
  if (requestContext.current.context !== context) {
    requestContext.current = { context, epoch: requestContext.current.epoch + 1 };
  }
  const epoch = requestContext.current.epoch;
  const [quoteResult, setQuoteResult] = useState<{ epoch: number; value: any } | null>(null);
  const quote = pricingReady && quoteResult?.epoch === epoch ? quoteResult.value : null;
  const setQuote = (value: any) => setQuoteResult(value ? { epoch, value } : null);
  const [selectedIdentity, setSelectedIdentity] = useState(pricingIdentity);
  const [reviewedIdentity, setReviewedIdentity] = useState(() => readPricingReview(data.mode)?.identity ?? (pricingReady ? pricingIdentity : null));
  const [reviewRequired, setReviewRequired] = useState(() => readPricingReview(data.mode)?.required ?? false);
  const needsReview = cart.length > 0 && (reviewRequired || (reviewedIdentity !== null && reviewedIdentity !== pricingIdentity));
  useEffect(() => {
    if (!initialized && pricingReady) {
      setCart(readUnifiedCart(data.mode, channel));
      setInitialized(true);
      setReviewedIdentity(readPricingReview(data.mode)?.identity ?? pricingIdentity);
    }
  }, [initialized, pricingReady, data.mode, channel, pricingIdentity]);
  useEffect(() => {
    setSelected(null);
    setEditingIndex(null);
    setEditingFavorite(null);
    setSavedBusy(false);
    savedLocked.current = false;
    savedRequest.current += 1;
    if (initialized && cart.length && (!pricingReady || reviewedIdentity !== pricingIdentity)) setReviewRequired(true);
  }, [pricingReady, pricingIdentity]);
  useEffect(() => {
    if (initialized && !cart.length && pricingReady) { setReviewedIdentity(pricingIdentity); setReviewRequired(false); }
  }, [cart.length, pricingReady, pricingIdentity, initialized]);
  useEffect(() => {
    if (initialized && reviewedIdentity !== null) {
      localStorage.setItem(`tequisa-cart-${data.mode}-pricing`, JSON.stringify({ identity: reviewedIdentity, required: !!needsReview }));
    }
  }, [initialized, reviewedIdentity, needsReview, data.mode]);
  const pickupDetails = usePickupDetails(pickupProfileKey(data.mode, data.pricingIdentity));
  const name = pickupDetails.name;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checkoutKey, setCheckoutKey] = useState(uid());
  const submitting = useRef(false);
  const pendingPrices = cart.some(item => products.find(p => p.id === item.productId)?.browseOnly);
  useEffect(() => {
    if (!initialized) return;
    localStorage.setItem(
      "tequisa-cart-" + data.mode,
      JSON.stringify(cart),
    );
    setQuote(null);
    setError("");
    setCheckoutKey(uid());
    if (pricingReady && cart.length && !pendingPrices) {
      let live = true;
      data
        .quote(channel, cart)
        .then((q: any) => {
          if (!live || requestContext.current.epoch !== epoch) return;
          if (q.channel !== channel) { setError(t('cart.accessChanged')); return; }
          setQuote(q);
        })
        .catch((e: any) => live && requestContext.current.epoch === epoch && setError(translateMessage(e)));
      return () => {
        live = false;
      };
    }
  }, [context, pendingPrices, initialized]);
  const cards = groupMenuProducts(products);
  const searched = cards.filter(p => p.name.toLowerCase().includes(search.trim().toLowerCase()));
  const productCategories = [...new Set(searched.map(p => p.category))];
  const categories = [
    ALL,
    ...MENU_SECTIONS.filter(name => productCategories.includes(name)),
    ...productCategories.filter((name) => !MENU_SECTIONS.includes(name)),
  ];
  const activeCategory = categories.includes(category) ? category : ALL;
  // A removed category must not silently reselect itself when the catalog/search changes again.
  useEffect(() => { if (category !== activeCategory) setCategory(activeCategory); }, [category, activeCategory]);
  const filtered = searched.filter(p => activeCategory === ALL || p.category === activeCategory);
  const count = cart.reduce((n, i) => n + i.quantity, 0);
  const total =
    quote?.total ??
    quote?.totalAmount ??
    cart.reduce((n, i) => n + i.unitPrice * i.quantity, 0);
  async function addSavedItems(items: any[]) {
    items = items.map(item => ({ ...item, selections: selectionsForChannel(channel, item.selections) }));
    if (!pricingReady || savedLocked.current || busy || submitting.current) return false;
    const requestEpoch = requestContext.current.epoch;
    if (items.some(item => !validateSavedItem(item, products).valid)) return false;
    if (items.some(item => products.find(p => p.id === item.productId)?.browseOnly)) {
      setError(t('saved.pendingPrice')); return false;
    }
    const requestId = ++savedRequest.current;
    savedLocked.current = true;
    setSavedBusy(true);
    try {
      const result = await data.quote(channel, items);
      if (requestContext.current.epoch !== requestEpoch) return false;
      if (result.channel !== channel) { setError(t('cart.accessChanged')); return false; }
      if (!Array.isArray(result.lines) || result.lines.length !== items.length || result.lines.some((line: any, index: number) => line.quantity !== items[index].quantity || canonicalItem(line) !== canonicalItem(items[index]) || !Number.isFinite(line.unitPrice))) throw new Error(t('saved.reviewRequired'));
      const lines = items.map((item, index) => ({ ...item, selections: structuredClone(item.selections),
        name: products.find(p => p.id === item.productId)?.name,
        unitPrice: result.lines[index].unitPrice, optionLabels: result.lines[index].optionLabels }));
      setCart(current => [...current, ...lines]);
      return true;
    } catch (error) { if (requestContext.current.epoch === requestEpoch) setError(translateMessage(error)); return false; }
    finally { if (savedRequest.current === requestId) { savedLocked.current = false; setSavedBusy(false); } }
  }
  async function checkout() {
    if (submitting.current || (data.mode === 'live' && data.paymentAvailable !== true) || !pricingReady || needsReview || !data.open || !quote || pendingPrices) return;
    const ownerEpoch = checkoutOwner.current.epoch;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const fresh = await data.quote(channel, cart);
      if (requestContext.current.epoch !== epoch) return;
      if (fresh.channel !== channel) { setQuote(null); setReviewRequired(true); setError(t('cart.accessChanged')); return; }
      if (quote && fresh.fingerprint !== quote.fingerprint) {
        setQuote(fresh);
        setReviewRequired(true);
        setError(t("cart.priceChanged"));
        return;
      }
      if(!paymentReadyRef.current)return;
      const res = await data.checkout({
        channel,
        items: cart,
        acceptedFingerprint: fresh.fingerprint,
        idempotencyKey: checkoutKey,
        pickupName: name.trim(),
      });
      if (checkoutOwner.current.epoch !== ownerEpoch) return;
      rememberCurrentOrder(currentOrderKey(data.mode, data.pricingIdentity), res.trackingCredential);
      setCart([]);
      go("/receipt/" + encodeURIComponent(res.trackingCredential));
    } catch (e: any) {
      setError(translateMessage(e));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }
  return (
    <div className="menu-layout">
      <section>
        <div className="intro">
          <div>
            <h1>{data.settings?.name?.trim() || t("menu.title")}</h1>
          </div>
          <div className="open-tag">
            <span className="dot" />{" "}
            {!data.open ? t(data.settings?.paused ? 'menu.paused' : 'menu.closed') : t("menu.open")}
          </div>
        </div>
        {(!data.open || (data.mode === 'live' && data.paymentAvailable !== true)) && <div className="notice menu-status" role="status">
          {!data.open && <p>{t(data.settings?.paused ? 'menu.pausedNotice' : 'menu.closedNotice')}</p>}
          {products.some((p: any) => p.browseOnly) && <p>{t('menu.pricesPending')}</p>}
          {data.mode === 'live' && data.paymentAvailable !== true && <p>{t('payment.unavailable')}</p>}
        </div>}
        <p className="menu-hours">{data.settings?.openingHours ? hoursLabel(locale, data.settings.openingHours) : t("menu.hours")}</p>
        <p className="menu-sizes">{t("menu.cupSizes")}</p>
        <div className="toolbar">
          <div className="tabs" role="group" aria-label={t('menu.categories')}>
            {categories.map((c) => (
              <button
                key={String(c)}
                type="button"
                className={activeCategory === c ? "active" : ""}
                aria-pressed={activeCategory === c}
                aria-controls="menu-products"
                onClick={() => setCategory(String(c))}
              >
                {c === ALL ? t("common.all") : c === "Coffee" ? t("menu.coffee") : String(c)}
              </button>
            ))}
          </div>
          <div className="search">
            <Search size={14} />
            <input
              aria-label={t("menu.searchLabel")}
              placeholder={t("menu.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="section-title">
          <h2>{activeCategory === ALL ? t("menu.discover") : activeCategory === "Coffee" ? t("menu.coffee") : activeCategory}</h2>
          <span role="status" aria-live="polite" aria-atomic="true">{pricingReady ? t("menu.choiceCount", { count: filtered.length }) : t('common.loadingStation')}</span>
        </div>
        {!pricingReady && <p role="status">{t('provider.loadingAccess')}</p>}
        <div className="product-grid" id="menu-products" aria-busy={!pricingReady}>
          {(pricingReady ? filtered : []).map((card, i) => {
            const choose = (product: any) => {
              setEditingIndex(null);
              setEditingFavorite(null);
              setSelectedIdentity(pricingIdentity);
              setSelected(product);
            };
            return <MenuProductCard key={`${pricingIdentity}:${card.id}`} card={card} index={i} channel={channel} data={data} disabled={busy || !pricingReady} onChoose={choose} />;
          })}
        </div>
        {pricingReady && !filtered.length && (
          <div className="empty">
            {t(search.trim() ? "menu.noResults" : "menu.emptyCategory")}
          </div>
        )}
        {pricingReady && <SavedDrinks key={savedKey} saved={saved} products={products} busy={busy || savedBusy}
          onAdd={addSavedItems} onEdit={favorite => {
            setEditingIndex(null); setEditingFavorite(favorite); setSelectedIdentity(pricingIdentity);
            setSelected(products.find(p => p.id === favorite.item.productId));
          }} />}
        {pricingReady && <MenuFeatures products={products} canReadRecipes={!!data.canReadRecipes} channel={channel} />}
      </section>
      <aside id="cart" className="cart" aria-busy={busy}>
        <div className="cart-head">
          <h2>{t("cart.title")}</h2>
          <span className="badge">
            <ShoppingBag size={12} /> {count} item
          </span>
        </div>
        <div className="cart-content">
          {!cart.length ? (
            <div className="cart-empty">
              <ShoppingBag />
              <h3>{t("cart.emptyTitle")}</h3>
              <p>{t("cart.emptyText")}</p>
            </div>
          ) : (
            cart.map((item, idx) => (
              <div className="cart-item" key={idx}>
                <div className="item-top">
                  <strong>{drinkName(locale, item.name)}</strong>
                  <span>
                    {quote
                      ? money(
                          quote.lines?.[idx]?.lineTotal ??
                            item.unitPrice * item.quantity,
                        )
                      : t(pendingPrices ? "cart.pricePending" : "cart.calculating")}
                  </span>
                </div>
                <p>
                  {(quote?.lines?.[idx]?.optionLabels ?? item.optionLabels)
                    ?.map(translateOptionLabel).join(" · ") || item.summary}
                </p>
                <button
                  className="cart-edit"
                  disabled={busy || !pricingReady || !products.some((p) => p.id === item.productId)}
                  onClick={() => {
                    setEditingFavorite(null);
                    setEditingIndex(idx);
                    setSelectedIdentity(pricingIdentity);
                    setSelected(products.find((p) => p.id === item.productId));
                  }}
                >
                  {t("common.edit", { name: drinkName(locale, item.name) })}
                </button>
                <div className="quantity">
                  <button
                    disabled={busy}
                    aria-label={t("cart.decrease", { name: drinkName(locale, item.name) })}
                    onClick={() =>
                      setCart(
                        cart.flatMap((c, i) =>
                          i === idx
                            ? c.quantity > 1
                              ? [{ ...c, quantity: c.quantity - 1 }]
                              : []
                            : [c],
                        ),
                      )
                    }
                  >
                    <Minus size={11} />
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    disabled={busy}
                    aria-label={t("cart.increase", { name: drinkName(locale, item.name) })}
                    onClick={() =>
                      setCart(
                        cart.map((c, i) =>
                          i === idx
                            ? { ...c, quantity: Math.min(20, c.quantity + 1) }
                            : c,
                        ),
                      )
                    }
                  >
                    <Plus size={11} />
                  </button>
                  <button
                    className="remove"
                    disabled={busy}
                    aria-label={t("cart.remove", { name: drinkName(locale, item.name) })}
                    onClick={() => setCart(cart.filter((_, i) => i !== idx))}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))
          )}
          <div className="totals">
            <div className="total-row">
              <span>{t("cart.subtotal", { count: count ? ` (${count} ${t("common.item")})` : "" })}</span>
              <span>{pendingPrices ? t("cart.pricePending") : cart.length && !quote ? t("cart.calculating") : money(total)}</span>
            </div>
            <div className="total-row">
              <span>
                {channel === "employee"
                  ? t("cart.employeePricing")
                  : t("cart.publicPricing")}
              </span>
              <span>{t("common.included")}</span>
            </div>
            <div className="total-row strong">
              <span>{t("common.total")}</span>
              <span>{pendingPrices ? t("cart.pricePending") : cart.length && !quote ? t("cart.calculating") : money(total)}</span>
            </div>
          </div>
          {cart.length > 0 && (
            <PickupDetails details={pickupDetails} busy={busy} />
          )}
          {error && (
            <div role="alert" className="error">
              {error}
            </div>
          )}
          {needsReview && (
            <div className="notice" role="status">
              <p>{t('cart.accessChanged')}</p>
              <button type="button" disabled={!quote || busy || !pricingReady} onClick={() => {
                setReviewedIdentity(pricingIdentity);
                setReviewRequired(false);
              }}>{t('cart.reviewPrices')}</button>
            </div>
          )}
          <button
            style={{ marginTop: 18 }}
            className="primary full"
            disabled={(data.mode === 'live' && data.paymentAvailable !== true) ||
              !cart.length || !name.trim() || !quote || busy || !data.open || pendingPrices || !pricingReady || needsReview
            }
            onClick={checkout}
          >
            {busy
              ? t("cart.preparingPayment")
              : data.mode === "demo"
                ? t("cart.simulateOrder")
                : t("cart.continuePayment")}
            <ArrowRight size={15} />
          </button>
          <p className="fine">
            {data.mode === "demo"
              ? t("cart.demoPaymentNote")
              : data.paymentAvailable !== true ? t('payment.unavailable')
              : !data.open ? t(data.settings?.paused ? 'menu.pausedNotice' : 'menu.closedNotice')
              : t("cart.paymentNote")}
          </p>
        </div>
        <div className="cart-footer">
          <ShieldCheck size={20} />
          <span>
            {channel === "employee"
              ? t("cart.employeeFooter")
              : t("cart.publicFooter")}
            <br />
            {t("cart.pickupFooter")}
          </span>
        </div>
      </aside>
      {count > 0 && (
        <button
          className="primary mobile-cart"
          onClick={() =>
            document
              .getElementById("cart")
              ?.scrollIntoView({ behavior: "smooth" })
          }
        >
          <span>
            <ShoppingBag size={14} /> {count} {t("common.item")} · {t("cart.view")}
          </span>
          <strong>{pendingPrices ? t("cart.pricePending") : cart.length && !quote ? t("cart.calculating") : money(total)}</strong>
        </button>
      )}
      {selected && pricingReady && selectedIdentity === pricingIdentity && (
        <Customize
          product={selected}
          initialItem={editingFavorite?.item ?? (editingIndex === null ? undefined : cart[editingIndex])}
          editingFavorite={!!editingFavorite}
          onFavorite={(item: any) => saved.save(item, products.find(p => p.id === item.productId)?.name ?? selected.name, editingFavorite?.id)}
          data={data}
          channel={channel}
          onClose={() => setSelected(null)}
          onAdd={(item: any) => {
            if (submitting.current) return;
            setCart((current) =>
              editingIndex === null
                ? [...current, item]
                : current.map((line, index) => index === editingIndex ? item : line),
            );
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}
function Customize({ product, initialItem, editingFavorite, onFavorite, data, channel, onClose, onAdd }: any) {
  const [variantId, setVariantId] = useState(product.id);
  const menuProducts = useMemo(() => customerMenu(data.menuProducts || []), [data.menuProducts]);
  const p = menuProducts.find((candidate: any) => candidate.id === variantId) ?? { ...product, available: false };
  const variants = product.drink ? menuProducts.filter((candidate: any) => candidate.drink?.id === product.drink.id).sort((a: any, b: any) => temperatures.indexOf(a.temperature) - temperatures.indexOf(b.temperature)) : [];
  const { t, money, locale, translateMessage, translateOptionLabel } = useI18n();
  const [selections, setSelections] = useState<any>(() => selectionsForChannel(channel, initialItem?.selections ?? p.defaults));
  const [quote, setQuote] = useState<any>(null);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState<number>(initialItem?.quantity ?? 1);
  const [optionsReset, setOptionsReset] = useState(false);
  const [favoriteMessage, setFavoriteMessage] = useState('');
  useEffect(() => {
    let live = true;
    setQuote(null);
    setError("");
    if (p.browseOnly) return;
    data
      .quote(channel, [{ productId: p.id, quantity, selections }])
      .then((q: any) => {
        if (!live) return;
        if (q.channel !== channel) { setError(t('cart.accessChanged')); return; }
        setQuote(q);
      })
      .catch((e: any) => live && setError(translateMessage(e)));
    return () => {
      live = false;
    };
  }, [selections, quantity, p]);
  function option(key: string, value: any) {
    setSelections({ ...selections, [key]: value });
  }
  const total = quote?.total ?? quote?.totalAmount ?? p.price;
  const options = p.options || {};
  return (
    <Dialog title={drinkName(locale, p.drink?.name ?? p.name)} onClose={onClose}>
      <MenuDescription name={p.drink?.name ?? p.name} description={p.drink?.description ?? p.description} />
      <p>{t("menu.cupSizes")}</p>
      {!!variants.length && <div className="choices temperature-choices" aria-label={t('temperature.label')}>{variants.map((variant: any) => <button key={variant.id} aria-pressed={variant.id === p.id} className={variant.id === p.id ? 'selected' : ''} disabled={!variant.available} onClick={() => {
        if (variant.id === p.id) return;
        setOptionsReset(true);
        setVariantId(variant.id);
        setSelections(selectionsForChannel(channel, structuredClone(variant.defaults)));
        setQuote(null);
        setError('');
      }}>{t(`temperature.${variant.temperature}`)}{!variant.available && ` · ${t('temperature.soldOut')}`}</button>)}</div>}
      <p role="status" aria-live="polite">{optionsReset ? t('customize.optionsReset') : ''}</p>
      {p.filterSelection && <fieldset className="bean-selection"><legend>{t('customize.beans')}</legend><div className="bean-cards">{(options.beans || []).map((bean: any) => <button key={bean.id} className={`bean-card ${selections.beanId === bean.id ? 'selected' : ''}`} aria-pressed={selections.beanId === bean.id} disabled={bean.available === false} onClick={() => option('beanId', bean.id)}>
        {bean.imageUrl && <img src={bean.imageUrl} alt="" onError={e => { e.currentTarget.style.display = "none"; }} />}<strong>{bean.name}</strong>{bean.roaster && <span>{bean.roaster}</span>}{bean.origin && <span>{bean.origin}</span>}<span>{[bean.process, bean.roastLevel].filter(Boolean).join(' · ')}</span>{bean.tastingNotes && <span>{bean.tastingNotes}</span>}{bean.description && <small>{bean.description}</small>}{bean.available === false && <span>{t('temperature.soldOut')}</span>}
      </button>)}</div></fieldset>}
      {[
        ["beanId", t("customize.beans"), p.filterSelection ? [] : options.beans || []],
        [
          "ice",
          t("customize.ice"),
          (p.temperature === "hot" ? [] : options.ice || []).map((x: any) => ({
            id: x,
            name:
              (
                {
                  none: t("option.noIce"),
                  less: t("option.lessIce"),
                  normal: t("option.normalIce"),
                  extra: t("option.extraIce"),
                } as any
              )[x] || x,
          })),
        ],
        [
          "sweetness",
          t("customize.sweetness"),
          (options.sweetness?.length > 1 ? options.sweetness : []).map((x: any) => ({ id: x, name: x === 0 ? t("customize.noSweetener") : x + "%" })),
        ],
        [
          "packaging",
          t("customize.packaging"),
          channel === "employee" ? [
            { id: "takeaway", name: t("option.takeaway") },
            { id: "own_cup", name: t("option.ownCup") },
          ] : [],
        ],
      ].map(
        ([key, label, values]: any) =>
          values.length > 0 && (
            <div key={key}>
              <label>{label}</label>
              <div className="choices">
                {values.map((v: any) => (
                  <button
                    aria-pressed={selections[key] === v.id}
                    className={selections[key] === v.id ? "selected" : ""}
                    key={v.id}
                    onClick={() => option(key, v.id)}
                  >
                    {translateOptionLabel(v.name)}
                  </button>
                ))}
              </div>
            </div>
          ),
      )}
      <p className="sweetness-help">{t(options.sweetness?.length > 1 ? "customize.sweetnessHelp" : "customize.fixedSweetness")}</p>
      {options.extras?.length > 0 && (
        <>
          <label>{t("customize.extras")}</label>
          <div className="choices">
            {options.extras.map((v: any) => (
              <button
                key={v.id}
                aria-pressed={(selections.extras || []).includes(v.id)}
                className={
                  (selections.extras || []).includes(v.id) ? "selected" : ""
                }
                onClick={() =>
                  option(
                    "extras",
                    (selections.extras || []).includes(v.id)
                      ? selections.extras.filter((id: string) => id !== v.id)
                      : [...(selections.extras || []), v.id],
                  )
                }
              >
                {(selections.extras || []).includes(v.id) && <Check size={14} aria-hidden="true" />} {translateOptionLabel(v.name)}
              </button>
            ))}
          </div>
        </>
      )}
      <div className="customize-quantity-row">
        <span id="drink-quantity-label">{t("customize.quantity")}</span>
        <div className="quantity customize-quantity" role="group" aria-labelledby="drink-quantity-label">
          <button type="button" aria-label={t("customize.decreaseQuantity")} disabled={quantity <= 1}
            onClick={() => setQuantity(current => Math.max(1, current - 1))}>
            <Minus size={18} aria-hidden="true" />
          </button>
          <output id="drink-quantity" aria-label={t("customize.quantity")} aria-live="polite" aria-atomic="true">{quantity}</output>
          <button type="button" aria-label={t("customize.increaseQuantity")} disabled={quantity >= 20}
            onClick={() => setQuantity(current => Math.min(20, current + 1))}>
            <Plus size={18} aria-hidden="true" />
          </button>
        </div>
      </div>
      {channel === "employee" && quote && (
        <details className="breakdown">
          <summary>{t("customize.costBreakdown")}</summary>
          {Object.entries(quote.lines?.[0]?.costs || {})
            .map(([name, amount]) => ({
              name:
                (
                  {
                    ingredients: t("customize.ingredients"),
                    equipment: t("customize.equipment"),
                    packaging: t("customize.packaging"),
                  } as any
                )[name] || name,
              amount,
            }))
            .map((c: any, i: number) => (
              <div className="total-row" key={i}>
                <span>{c.name || c.label}</span>
                <span>{money(c.amount ?? c.cost)}</span>
              </div>
            ))}
          <p>
            {t("customize.costNote")}
          </p>
        </details>
      )}
      {initialItem && !validateSavedItem({ productId: p.id, quantity, selections }, menuProducts).valid && <div className="notice" role="alert">
        <p>{t('saved.reviewRequired')}</p>
        <button className="secondary" onClick={() => { setSelections(selectionsForChannel(channel, structuredClone(p.defaults))); setFavoriteMessage(''); }}>{t('saved.resetOptions')}</button>
      </div>}
      {onFavorite && <button className="secondary full" disabled={!p.available || (!quote && !p.browseOnly) || !validateSavedItem({ productId: p.id, quantity, selections }, menuProducts).valid} onClick={() => {
        const item = { productId: p.id, quantity, selections: structuredClone(selections) };
        if (!validateSavedItem(item, menuProducts).valid) { setFavoriteMessage(t('saved.reviewRequired')); return; }
        setFavoriteMessage(t(onFavorite(item) ? 'saved.favoriteSaved' : 'saved.storageError'));
      }}>{t(editingFavorite ? 'saved.favoriteUpdate' : 'saved.favoriteSave')}</button>}
      {favoriteMessage && <p role="status">{favoriteMessage}</p>}
      {error && <p className="error">{error}</p>}
      <button
        className="primary full"
        disabled={!p.available || (!quote && !p.browseOnly) || !validateSavedItem({ productId: p.id, quantity, selections }, menuProducts).valid}
        onClick={() =>
          onAdd({
            productId: p.id,
            name: p.name,
            quantity,
            selections,
            unitPrice: p.browseOnly ? null : total / quantity,
            optionLabels: quote?.lines?.[0]?.optionLabels ?? [
              options.beans?.find((b: any) => b.id === selections.beanId)?.name,
              selections.ice !== "none" ? ({normal:"Es normal",less:"Sedikit es",extra:"Ekstra es"} as any)[selections.ice] : undefined,
              options.sweetness?.length > 1 ? `${selections.sweetness}%` : undefined,
              t(selections.packaging === "own_cup" ? "option.ownCup" : "option.takeaway"),
              ...(options.extras || []).filter((e: any) => selections.extras.includes(e.id)).map((e: any) => e.name),
            ].filter(Boolean),
          })
        }
      >
        {t(initialItem && !editingFavorite ? "customize.update" : "customize.add", { price: p.browseOnly ? t("cart.pricePending") : money(total) })}
        <Plus size={15} />
      </button>
    </Dialog>
  );
}
